// MEMORY-QUALITY-WATCH (design 2026-10-31 §6): memory's health as numbers. The hourly
// `memory.quality` job samples every company, space and writer; a company's admin reads the latest
// on Setup › Memory › Health (`MemoryQualityView`, part of `MemoryAdminHealth`); the platform's
// super admins read the findings and set the thresholds per company in the operator hub
// (`OperatorMemoryQuality`). A finding is operator data: no company ever reads one.
import { z } from "zod";
import {
  MemoryQualityFindingKind,
  MemoryQualityFindingSource,
  MemoryQualityFindingState,
} from "../enums";
import { boundedRecord, Timestamp, Uuid } from "../primitives";
import { defineRoute } from "./route";

/**
 * The thresholds an alert is raised at. A value above the threshold raises (`why_share_min` is the
 * floor: below it raises). Defaults ship with the platform; an operator overrides one per company.
 */
export const MEMORY_QUALITY_THRESHOLDS = [
  {
    key: "flood_per_hour",
    label: "Records per writer per hour",
    unit: "records",
    default: 40,
    dir: "above",
  },
  {
    key: "flood_median_multiple",
    label: "Writer above its own median by",
    unit: "times",
    default: 3,
    dir: "above",
  },
  {
    key: "duplicate_share",
    label: "Duplicate share of writes",
    unit: "share",
    default: 0.1,
    dir: "above",
  },
  {
    key: "contradictions_open",
    label: "Open contradictions",
    unit: "pairs",
    default: 20,
    dir: "above",
  },
  {
    key: "contradiction_age_days",
    label: "Oldest open contradiction",
    unit: "days",
    default: 14,
    dir: "above",
  },
  {
    key: "stale_proposals",
    label: "Proposals waiting past the review window",
    unit: "proposals",
    default: 30,
    dir: "above",
  },
  {
    key: "stale_facts",
    label: "Records out of date against facts of record",
    unit: "records",
    default: 0,
    dir: "above",
  },
  {
    key: "brief_chars",
    label: "Memory brief delivered per run",
    unit: "characters",
    default: 6000,
    dir: "above",
  },
  {
    key: "reads_per_run",
    label: "Memory reads per run",
    unit: "reads",
    default: 12,
    dir: "above",
  },
  {
    key: "why_share_min",
    label: "Saves that carry a why",
    unit: "share",
    default: 0.5,
    dir: "below",
  },
] as const;

export type MemoryQualityThresholdKey = (typeof MEMORY_QUALITY_THRESHOLDS)[number]["key"];
export const MEMORY_QUALITY_THRESHOLD_KEYS = MEMORY_QUALITY_THRESHOLDS.map((t) => t.key) as [
  MemoryQualityThresholdKey,
  ...MemoryQualityThresholdKey[],
];
export const MemoryQualityThresholdKeySchema = z.enum(MEMORY_QUALITY_THRESHOLD_KEYS);

/** The measures of one scope for one hour; `null` is "not measurable yet", never zero. */
export const MemoryQualityNumbers = z
  .object({
    writes: z.int().min(0),
    duplicate_share: z.number().min(0).max(1).nullable(),
    typed_writes: z.int().min(0),
    excerpt_events: z.int().min(0).nullable(),
    excerpt_share: z.number().min(0).max(1).nullable(),
    why_share: z.number().min(0).max(1).nullable(),
    contradictions_open: z.int().min(0).nullable(),
    oldest_contradiction_days: z.int().min(0).nullable(),
    stale_proposals: z.int().min(0).nullable(),
    brief_chars: z.int().min(0).nullable(),
    reads_per_run: z.number().min(0).nullable(),
    stale_facts: z.int().min(0).nullable(),
    /** A writer past its limit this hour (the flood alert). */
    flood: z.boolean(),
  })
  .meta({ id: "MemoryQualityNumbers" });
export type MemoryQualityNumbers = z.infer<typeof MemoryQualityNumbers>;

/** What the Health page shows: the latest sample, in short labels. */
export const MemoryQualityView = z
  .object({
    /** The hour of the latest sample; null before the first run. */
    as_of: Timestamp.nullable(),
    company: MemoryQualityNumbers.nullable(),
    spaces: z.array(
      z.object({
        key: z.string(),
        name: z.string(),
        writes: z.int().min(0),
        duplicate_share: z.number().min(0).max(1).nullable(),
        stale_proposals: z.int().min(0).nullable(),
        contradictions_open: z.int().min(0).nullable(),
      }),
    ),
    writers: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        writes: z.int().min(0),
        duplicate_share: z.number().min(0).max(1).nullable(),
        flood: z.boolean(),
      }),
    ),
    /** The facts-of-record check: records that say what structure no longer says. */
    facts: z.object({
      /** Null until the check has looked (not measured is not none). */
      stale_records: z.int().min(0).nullable(),
      suggestions_open: z.int().min(0),
      checked_at: Timestamp.nullable(),
    }),
    /** Measures that wait on something not live yet (each one named in short words). */
    waiting: z.array(z.enum(["excerpts", "why", "brief", "reads", "member_reads"])),
  })
  .meta({ id: "MemoryQualityView" });
export type MemoryQualityView = z.infer<typeof MemoryQualityView>;

const HubCompany = z.object({ id: Uuid, slug: z.string(), name: z.string() });

export const MemoryQualityFindingView = z
  .object({
    id: Uuid,
    source: MemoryQualityFindingSource,
    kind: MemoryQualityFindingKind,
    scope_key: z.string(),
    severity: z.enum(["critical", "high", "medium", "low"]),
    state: MemoryQualityFindingState,
    title: z.string(),
    suggestion: z.string().nullable(),
    value: z.number().nullable(),
    threshold: z.number().nullable(),
    /** Counts and record pointers behind it (never raw chats). */
    detail: z.record(z.string(), z.unknown()),
    first_seen_at: Timestamp,
    last_seen_at: Timestamp,
  })
  .meta({ id: "MemoryQualityFindingView" });
export type MemoryQualityFindingView = z.infer<typeof MemoryQualityFindingView>;

export const OperatorMemoryQuality = z
  .object({
    company: HubCompany,
    as_of: Timestamp.nullable(),
    metrics: MemoryQualityNumbers.nullable(),
    thresholds: z.array(
      z.object({
        metric: MemoryQualityThresholdKeySchema,
        label: z.string(),
        unit: z.string(),
        dir: z.enum(["above", "below"]),
        value: z.number(),
        default: z.number(),
        custom: z.boolean(),
      }),
    ),
    /** Open first, then the last 30 resolved or dismissed. */
    findings: z.array(MemoryQualityFindingView),
    facts: z.object({
      stale_records: z.int().min(0).nullable(),
      suggestions_open: z.int().min(0),
      checked_at: Timestamp.nullable(),
    }),
  })
  .meta({ id: "OperatorMemoryQuality" });
export type OperatorMemoryQuality = z.infer<typeof OperatorMemoryQuality>;

/**
 * Set (a number) or clear (`null`: back to the platform default) a company's thresholds, and
 * dismiss findings the operator has dealt with.
 */
export const OperatorMemoryQualityPut = z
  .strictObject({
    // keys are threshold names (checked below); an exhaustive enum-keyed record would demand all
    thresholds: boundedRecord(
      z.number().min(0).max(1_000_000).nullable(),
      MEMORY_QUALITY_THRESHOLD_KEYS.length,
      z.string().max(60),
    )
      .refine(
        (r) => Object.keys(r).every((k) => (MEMORY_QUALITY_THRESHOLD_KEYS as string[]).includes(k)),
        "an unknown threshold",
      )
      .optional(),
    dismiss: z.array(Uuid).max(50).optional(),
  })
  .meta({ id: "OperatorMemoryQualityPut" });
export type OperatorMemoryQualityPut = z.infer<typeof OperatorMemoryQualityPut>;

const operator = { feature: "core", tag: "operator", access: "operator" } as const;
const params = z.object({ company_id: Uuid });

export const memoryQualityRoutes = {
  "operator.memoryQuality.get": defineRoute({
    ...operator,
    id: "operator.memoryQuality.get",
    method: "get",
    path: "/v1/operator/memory-quality/{company_id}",
    summary: "One company's memory-quality findings, thresholds and latest numbers (super admins)",
    description:
      "MEMORY-QUALITY-WATCH. The alerts the hourly watch raised and the proposals of the support " +
      "sweep, each with the fix it suggests, the thresholds in force (platform defaults and this " +
      "company's overrides) and the latest company-wide sample. Super admins only (404 to anyone " +
      "else); no company reads any of it.",
    params,
    response: { status: 200, schema: OperatorMemoryQuality },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.memoryQuality.put": defineRoute({
    ...operator,
    id: "operator.memoryQuality.put",
    method: "put",
    path: "/v1/operator/memory-quality/{company_id}",
    summary:
      "Set or clear a company's memory-quality thresholds, or dismiss findings (super admins)",
    params,
    body: OperatorMemoryQualityPut,
    // operator configuration: kept with who set it (`updated_by_user_id`, `resolved_by_user_id`),
    // like marking an agent-health report reviewed; no company event
    safe: true,
    response: { status: 200, schema: OperatorMemoryQuality },
    errors: ["not_found", "validation_failed"],
    emits: [],
  }),
} as const;
